/**
 * Display labels for entities whose name could not be resolved.
 *
 * Product UUIDs, item UUIDs and IOTA object IDs are internal identifiers, not
 * user-facing content (#258). The only places they legitimately appear are the
 * `/product/:id` URL and the href of the "Verify on Blockchain" link. Anywhere a
 * name is missing, show one of these instead of falling back to a raw id.
 *
 * Mirrors the server-side fallbacks in `backend/src/utils/resolve-product-name.ts`.
 */
export const UNNAMED_PRODUCT = 'Unnamed Product';
export const UNNAMED_ITEM = 'Unnamed Item';
export const UNKNOWN_COLLECTION = 'Unknown Collection';

/**
 * The product name field (#395). A collection created since #395 starts with
 * a text "Name" product field, flagged `isNameField`: its value is the
 * product's name, the headline shoppers see when they scan. The label is
 * fixed, so code can rely on it. Mirrors `backend/src/utils/name-field.ts`.
 */
export const NAME_FIELD_LABEL = 'Name';

/** The part of a collection field the name rules read. */
export interface NameFieldDef {
  name: string;
  isItemField?: boolean;
  isNameField?: boolean;
}

/** A field label compared loosely: trimmed, lower case, "_" and runs of spaces as one space. */
const normalizedLabel = (name: unknown): string =>
  typeof name === 'string' ? name.trim().toLowerCase().replace(/[\s_]+/g, ' ') : '';

/** True for a label that means the product's name: "Name" or "Product name" (any case). */
export const isNameLabel = (name: unknown): boolean => {
  const label = normalizedLabel(name);
  return label === 'name' || label === 'product name';
};

/**
 * The collection's flagged name field, if it has one (collections created
 * since #395). Its value is required on every product.
 */
export const flaggedNameField = <T extends NameFieldDef>(fields: readonly T[] | null | undefined): T | undefined =>
  (fields ?? []).find(f => !!f && !f.isItemField && f.isNameField === true);

/**
 * The product field whose value names a product: the collection's name field
 * (`isNameField`), else a field called "Name", then one called "Product name",
 * else — older collections — the first product field. One rule for every
 * screen; mirrors `productNameField` in `backend/src/utils/display-names.ts`.
 */
export const productNameField = <T extends NameFieldDef>(fields: readonly T[] | null | undefined): T | undefined => {
  const product = (fields ?? []).filter(f => !!f && !f.isItemField);
  const labelled = (label: string) => product.find(f => normalizedLabel(f.name) === label);
  return product.find(f => f.isNameField === true) ?? labelled('name') ?? labelled('product name') ?? product[0];
};

/**
 * A product's name from its field values (`{ name, value }`, matched by field
 * name), by the rule above. Without the collection's field list, the values
 * stand in for it (in collection order, all product fields). Undefined when
 * that field has no value, so callers pick their own fallback.
 */
export const productNameFromValues = (
  values: readonly { name: string; value?: string | null }[] | null | undefined,
  fields?: readonly NameFieldDef[] | null
): string | undefined => {
  const list = values ?? [];
  const field = productNameField(fields && fields.length > 0 ? fields : list);
  const value = field ? list.find(v => v.name === field.name)?.value : undefined;
  return value && value.trim() ? value.trim() : undefined;
};
